/**
 * Copyright (c) 2019 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import { compose, head } from 'ramda';
import cloneDeep from 'lodash/cloneDeep';
import { fuzzySearch } from '../../../helper/search';
import { MULTIPLE_STUDIES, MULTIPLE_ROLES } from '../../../const';

// lowerTrim :: String -> String
const lowerTrim = (x) => (x ? x.toLowerCase().trim() : '');

// maybeObject :: falsy value a => Object | a -> Object
const maybeObject = (obj) => obj || new Proxy({}, { get: () => '' });

// sort => (String, String) -> Int
const sort = (a, b) => {
  typeof a === 'string' && typeof b === 'string' && ([a, b] = [lowerTrim(a), lowerTrim(b)]);

  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

export const SORT_PEOPLE_BY = {
  name: ({ firstName: aFirst = '', lastName: aLast = '' }, { firstName: bFirst = '', lastName: bLast = '' }) =>
    sort(`${aFirst} ${aLast}`, `${bFirst} ${bLast}`),

  userName: (A, B) => {
    const { username: a = '' } = A;
    const { username: b = '' } = B;

    if (!a || !b) return 0;

    return sort(a, b);
  },

  role: (A, B) => {
    const { roles: rolesA } = A;
    const { roles: rolesB } = B;

    if (!rolesA || !rolesB) return 0;

    const a = rolesA.length === 0 ? '' : rolesA.length === 1 ? compose(maybeObject, head)(rolesA).name : MULTIPLE_ROLES;
    const b = rolesB.length === 0 ? '' : rolesB.length === 1 ? compose(maybeObject, head)(rolesB).name : MULTIPLE_ROLES;

    return sort(a, b);
  },

  study: (A, B) => {
    const { studies: studiesA } = A;
    const { studies: studiesB } = B;

    if (!studiesA || !studiesB) return 0;

    const a =
      studiesA.length === 0 ? '' : studiesA.length === 1 ? compose(maybeObject, head)(studiesA).name : MULTIPLE_STUDIES;

    const b =
      studiesB.length === 0 ? '' : studiesB.length === 1 ? compose(maybeObject, head)(studiesB).name : MULTIPLE_STUDIES;
    return sort(a, b);
  },

  createdOn: (A, B) => {
    const { createdOn: a } = A;
    const { createdOn: b } = B;

    if (!a || !b) return 0;

    return sort(new Date(b), new Date(a));
  }
};

// applyOrganizationFilterAndSort :: Object -> ([Objects] -> [Objects])
export const applyOrganizationFilterAndSort = ({
  organizationFilter: { value: orgFilter = 'All' } = {},
  peopleSortFn: { value: peopleSortBy } = {},
  searchBy: peopleFilterBy = '',
  hiredOrgGid
} = {}) =>
  compose(
    (organizations) =>
      !peopleFilterBy && peopleSortBy === 'default'
        ? organizations
        : organizations.map((organization) => ({
            ...organization,
            people: compose(
              peopleSortBy !== 'default' ? sortPeople(peopleSortBy) : (xs) => xs,
              peopleFilterBy ? filterPeople(peopleFilterBy) : (xs) => xs
            )(organization.people)
          })),
    (organizations) =>
      orgFilter === 'All'
        ? organizations
        : organizations.filter(({ id: organizationId }) => orgFilter === organizationId),
    (organizations) => {
      if (!hiredOrgGid) return organizations;

      const ownOrgIndex = organizations.findIndex(({ gid }) => gid === hiredOrgGid);

      ownOrgIndex !== -1 &&
        ([organizations[ownOrgIndex], organizations[0]] = [organizations[0], organizations[ownOrgIndex]]);

      return organizations;
    },
    (organizations) => (hiredOrgGid ? cloneDeep(organizations) : organizations)
  );

// sortPeople :: String -> [Object: a] -> [Object: a]
export const sortPeople = (sortFn) => (people) =>
  sortFn === 'default' || !SORT_PEOPLE_BY[sortFn] ? people : cloneDeep(people).sort(SORT_PEOPLE_BY[sortFn]);

// filterPeople :: String -> [Object: a] -> [Object: a]
export const filterPeople = (filterBy) => (people) => {
  const search = fuzzySearch({
    data: people.map((p) => {
      const roleName =
        (p.roles &&
          (p.roles.length === 0
            ? ''
            : p.roles.length === 1
              ? compose(maybeObject, head)(p.roles).roleNameDescription
              : MULTIPLE_ROLES)) ||
        '';
      const studyName =
        (p.studies &&
          (p.studies.length === 0
            ? ''
            : p.studies.length === 1
              ? compose(maybeObject, head)(p.studies).name
              : MULTIPLE_STUDIES)) ||
        '';

      return { ...p, fullName: `${p.firstName} ${p.lastName}`, roleName, studyName };
    }),
    searchKeys: ['firstName', 'lastName', 'fullName', 'username', 'roleName', 'studyName'],
    options: { caseSensitive: false }
  });

  return search(filterBy).map(({ fullName, ...rest }) => ({ ...rest }));
};

// maybeMultiple :: Object {() -> a, () -> b, () -> c} -> [*] -> String | a | b | c
export const maybeMultiple =
  ({ multiple = () => '', single = () => '', none = () => '' }) =>
  (xs) =>
    (!xs || !head(xs) ? none : xs.length > 1 ? multiple : single)();

export const paginatePeople = (people, currentPage, pageSize) => {
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  return people.slice(startIndex, endIndex);
};
